import { onUnmounted, ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { useToast } from '@negar/core';
import { i18n } from '@libs/i18n';
import { CODES, useSocket, useQueryClient } from '@libs/use';
import { omitBy, toShortDate } from '@libs/utils';
import {
  ERRORS_TO_SHOW,
  getOrderSideValue,
  IPO_REGISTERED_STATE_TITLE,
  LEGACY_ORDER_STATUSES,
  useGetServerClock,
  transformOrderAndTradeChange,
  isHeadlineTime,
  NEW_OMS_ORDER_STATUSES,
  transformNewOmsOrderAndTradeChange,
} from '@core/order';
import {
  createNewOmsUserChannelName,
  createUserChannelName,
  useUser,
} from '@core/user';
import { PORTFOLIO_QUERY_KEYS } from '@core/portfolio';

import { ORDER_SOCKET_ERROR_DESCRIPTION } from '@/modules/order';

import useWebEngage from '@/composables/useWebEngage';

const DEFAULT_ORDER_DAYS_COUNT = {
  lastOrderDay: '',
  ordersCount: 0,
};

export default function useUserLiveMessages() {
  const { t } = i18n.global;

  const channelsRef = ref();
  const newOmsChannelsRef = ref();

  const { socket, onReady } = useSocket();
  const { user, isUserReady } = useUser();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { webEngage } = useWebEngage();

  const { time, isErrorLoadingServerClock } = useGetServerClock({
    immediate: isUserReady,
  });

  const orderDaysStorage = useLocalStorage<typeof DEFAULT_ORDER_DAYS_COUNT>(
    'order-days-count',
    DEFAULT_ORDER_DAYS_COUNT
  );

  const onMessage = (signal) => {
    const actions = {
      [CODES.OmsOrderAndTradeSignal]: transformOrderAndTradeChange,
    };

    if (actions[signal.code]) {
      const data = actions[signal.code](signal.data);
      if (
        data.investorBourseCodeId === user.value?.investorBourseCodes[0].id &&
        !data.isAlgorithm
      ) {
        // Trade
        if (data.messageType === 'executeTrade') {
          const {
            tradeQuantity,
            instrumentName,
            tradePrice,
            orderSideId,
            instrumentIdentification,
          } = data;

          const orderDate = toShortDate(new Date());

          toast.positive({
            message: t('orders.successfully_trade_message', {
              tradeQuantity,
              instrumentName,
              tradePrice,
              side:
                getOrderSideValue(orderSideId) === 'sell'
                  ? t('orders.sold')
                  : t('orders.bought'),
            }),
            duration: 3000,
          });

          if (orderDaysStorage.value.lastOrderDay !== orderDate) {
            orderDaysStorage.value = {
              lastOrderDay: orderDate,
              ordersCount: orderDaysStorage.value.ordersCount + 1,
            };
          }

          webEngage?.track('Order_Placed', {
            Namad: instrumentName,
            Isin: instrumentIdentification,
            Value: tradeQuantity,
            'PAGE URL': '',
            Order_Type: 'Succeeded',
            NoValue: orderDaysStorage.value.ordersCount,
          });

          queryClient.invalidateQueries(PORTFOLIO_QUERY_KEYS.portfolio);
        }

        // Create order
        if (data.messageType === 'sendOrder') {
          if (data.orderStateId === LEGACY_ORDER_STATUSES.open.Confirmed) {
            toast.neutral({
              message: t('orders.successfully_enqueued_order_message'),
              duration: 3000,
            });
          } else if (
            ERRORS_TO_SHOW[data.orderStateEnglishTitle] &&
            (isErrorLoadingServerClock.value ||
              isHeadlineTime(time.value) ||
              !ORDER_SOCKET_ERROR_DESCRIPTION.includes(data.description))
          ) {
            toast.negative({
              message: data.description,
              duration: 3000,
            });
          }
        }

        // Cancel order
        if (data.messageType === 'cancelOrder') {
          if (data.orderStateId === LEGACY_ORDER_STATUSES.failed.Cancelled) {
            if (data.orderStateEnglishTitle === IPO_REGISTERED_STATE_TITLE) {
              if (ERRORS_TO_SHOW[data.orderStateEnglishTitle]) {
                return toast.negative({
                  message: data.description,
                  duration: 3000,
                });
              }

              return;
            }

            toast.neutral({
              message: t('orders.successfully_canceled_order_message'),
              duration: 3000,
            });
          }
        }

        // Edit order
        if (data.messageType === 'editOrder') {
          if (
            data.orderStateId ===
            LEGACY_ORDER_STATUSES.open.OrderModificationInBook
          ) {
            toast.neutral({
              message: t('orders.successfully_edit_order_message'),
              duration: 3000,
            });
          } else if (ERRORS_TO_SHOW[data.orderStateEnglishTitle]) {
            toast.negative({
              message: data.description,
              duration: 3000,
            });
          }
        }
      }
    }
  };

  const onMessageNewOms = (signal) => {
    const actions = {
      [CODES.NewOmsOrderAndTradeSignal]: transformNewOmsOrderAndTradeChange,
    };

    if (actions[signal.code]) {
      const data = omitBy(
        actions[signal.code](signal.data),
        (v) => v === null || v === undefined
      );

      if (!data.isAlgorithm) {
        // Trade
        if (data.messageType === 'executeTrade') {
          const {
            tradeQuantity,
            instrumentName,
            tradePrice,
            orderSideId,
            instrumentIdentification,
          } = data;

          const orderDate = toShortDate(new Date());

          toast.positive({
            message: t('orders.successfully_trade_message', {
              tradeQuantity,
              instrumentName,
              tradePrice,
              side:
                getOrderSideValue(orderSideId) === 'sell'
                  ? t('orders.sold')
                  : t('orders.bought'),
            }),
            duration: 3000,
          });

          if (orderDaysStorage.value.lastOrderDay !== orderDate) {
            orderDaysStorage.value = {
              lastOrderDay: orderDate,
              ordersCount: orderDaysStorage.value.ordersCount + 1,
            };
          }

          webEngage?.track('Order_Placed', {
            Namad: instrumentName,
            Isin: instrumentIdentification,
            Value: tradeQuantity,
            'PAGE URL': '',
            Order_Type: 'Succeeded',
            NoValue: orderDaysStorage.value.ordersCount,
          });
        }

        // Create order
        if (data.messageType === 'sendOrder') {
          if (data.orderStateId === NEW_OMS_ORDER_STATUSES.open.InBook) {
            toast.neutral({
              message: t('orders.successfully_enqueued_order_message'),
              duration: 3000,
            });
          }
        }

        // Cancel order
        if (data.messageType === 'cancelOrder') {
          if (data.orderStateId === NEW_OMS_ORDER_STATUSES.failed.Cancelled) {
            if (data.orderStateEnglishTitle === IPO_REGISTERED_STATE_TITLE) {
              return toast.negative({
                message: data.description,
                duration: 3000,
              });
            }

            toast.neutral({
              message: t('orders.successfully_canceled_order_message'),
              duration: 3000,
            });
          }
        }

        // Edit order
        if (data.messageType === 'editOrder') {
          if (
            data.orderStateId === NEW_OMS_ORDER_STATUSES.open.ModifiedAndInBook
          ) {
            toast.neutral({
              message: t('orders.successfully_edit_order_message'),
              duration: 3000,
            });
          }
        }

        // Error order
        if (
          data.messageType === 'ErrorOrder' &&
          (isErrorLoadingServerClock.value ||
            isHeadlineTime(time.value) ||
            !ORDER_SOCKET_ERROR_DESCRIPTION.includes(data.errorDescription))
        ) {
          toast.negative({
            message:
              data.errorDescription || t('orders.errorCreatingOrderMessage'),
            duration: 3000,
          });
        }
      }
    }
  };

  onReady(({ user }) => {
    const name = createUserChannelName(user.value.userName);
    const newOmsChannelName = createNewOmsUserChannelName(user.value.userId);

    channelsRef.value = socket.value?.subscribe(name, { batch: true });
    channelsRef.value.watch(onMessage);

    newOmsChannelsRef.value = socket.value?.subscribe(newOmsChannelName, {
      batch: true,
    });
    newOmsChannelsRef.value.watch(onMessageNewOms);
  });

  onUnmounted(() => {
    channelsRef.value.unsubscribe();
    channelsRef.value.unwatch();

    newOmsChannelsRef.value.unsubscribe();
    newOmsChannelsRef.value.unwatch();
  });
}
